import type { ActionContext, ActionExecutor } from "../TriggerEngine.interfaces";

const setProtectedMode = async (
  enabled: boolean,
  emitAction: ActionContext["emitAction"],
) => {
  if (!window.electronAPI?.setContentProtection) {
    throw new Error("Content protection requires the desktop application");
  }

  const result = await window.electronAPI.setContentProtection(enabled);

  if (
    !result?.success ||
    (result.enabled !== undefined && result.enabled !== enabled)
  ) {
    throw new Error(result?.error || "Could not change content protection");
  }

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");
  const db = await openDatabase();
  const config = await db.config.limit(1).first();

  if (config) {
    const updated = {
      ...config,
      configuration: {
        ...config.configuration,
        experiments: {
          ...config.configuration?.experiments,
          desktopContentProtection: enabled,
        },
      },
    };
    await db.config.update(config.id, updated);
    await saveSnapshotDatabase();
  }

  emitAction("protected_mode_changed", { enabled });
};

export const enableProtectedMode: ActionExecutor = async (
  _radio,
  _config,
  { emitAction },
) => setProtectedMode(true, emitAction);

export const disableProtectedMode: ActionExecutor = async (
  _radio,
  _config,
  { emitAction },
) => setProtectedMode(false, emitAction);

export const enableSystemAudio: ActionExecutor = async (
  radio,
  _config,
  { emitAction },
) => {
  if (!radio.SystemAudio.isCapturing()) {
    const started = await radio.SystemAudio.startCapture();

    if (!started) {
      throw new Error("Could not start system audio capture");
    }
  }

  if (!radio.SystemAudio.isCapturing()) {
    throw new Error("System audio capture is no longer available");
  }

  radio.SystemAudio.setMuted(false);
  if (radio.SystemAudio.getState().volume === 0) {
    radio.SystemAudio.setVolume(0.4);
  }

  const state = radio.SystemAudio.getState();
  emitAction("system_audio_changed", {
    capturing: state.isCapturing,
    muted: state.muted,
    volume: state.volume,
  });
};

export const disableSystemAudio: ActionExecutor = async (
  radio,
  _config,
  { emitAction },
) => {
  radio.SystemAudio.setMuted(true);
  emitAction("system_audio_changed", {
    capturing: radio.SystemAudio.isCapturing(),
    muted: true,
    volume: 0,
  });
};
